const validEvent = e => e && typeof e.id === 'string' && typeof e.symbol === 'string' && Number.isFinite(e.created) && Number.isFinite(e.close) && Number.isFinite(e.vwap)

const journalKey = userId => `erevis-vwap-notifications:${userId}`
const presentationState = value => Object.fromEntries(
  ['read','dismissed','cleared','popupDismissed'].filter(k=>value?.[k]===true).map(k=>[k,true])
)

// Keep acknowledgement with the receipt that can replay it, independently of
// the shared bell's bounded cache. Never change its cursor or delete a receipt.
export function persistVwapNotificationState(notifications, patch, storage) {
  try {
    storage ||= window.localStorage
    const owners=new Set(notifications.filter(n=>n.source==='vwap').map(n=>n.ownerUserId).filter(Boolean))
    for(const owner of owners) {
      const key=journalKey(owner), saved=JSON.parse(storage.getItem(key) || 'null')
      if(!Array.isArray(saved?.events))continue
      const ids=new Set(notifications.filter(n=>n.source==='vwap' && n.ownerUserId===owner).map(n=>n.id))
      const events=saved.events.map(e=>ids.has(`vwap:${owner}:${e.id}`)
        ? {...e,presentation:{...presentationState(e.presentation),...presentationState(patch)}} : e)
      storage.setItem(key,JSON.stringify({...saved,events}))
    }
  } catch { /* Same browser persistence boundary as the notification cache. */ }
}

export function vwapNotification(event, userId, now = Date.now()) {
  const historical = now - event.created * 1000 > 60000
  const acknowledged = presentationState(event.presentation)
  const side = event.condition === 'crosses_above' ? 'above' : event.condition === 'crosses_below' ? 'below' : 'across'
  const action = event.close === event.vwap ? 'touched' : `crossed ${side}`
  const when = new Date(event.created * 1000).toISOString()
  const details = [
    {label:'Symbol',value:event.symbol},
    {label:'Alert when',value:({crossing:'Crossing',crosses_above:'Crossing above',crosses_below:'Crossing below'})[event.condition] || event.condition},
    {label:'Trigger price',value:event.close.toFixed(4)},
    {label:'AVWAP at trigger',value:event.vwap.toFixed(4)},
    {label:'Trigger',value:event.trigger === 'every_minute' ? 'Every time' : event.trigger === 'once' ? 'Once only' : '—'},
  ]
  if (Number.isFinite(event.anchorTime)) details.push({label:'Anchor candle',value:new Date(event.anchorTime*1000).toLocaleString()})
  return {
    id: `vwap:${userId}:${event.id}`, source: 'vwap', ownerUserId: userId, type: 'vwap_alert',
    title: `${event.symbol} AVWAP alert`, symbol: event.symbol, tone: 'info',
    message: event.message || `Price ${event.close.toFixed(4)} ${action} AVWAP ${event.vwap.toFixed(4)}.`,
    createdAt: when, firedAt: when, details,
    read: acknowledged.read === true,
    ...acknowledged,
    popupDismissed: historical || acknowledged.popupDismissed === true || acknowledged.cleared === true || event.notifications?.toast === false,
    workspaceRoute: `/positions/workspace?symbol=${encodeURIComponent(event.symbol)}`,
  }
}

// Persist receipt and cursor together before presentation. A browser interruption
// can replay a saved receipt, but cannot acknowledge and lose its notification.
export function createVwapNotificationPoller({ userId, request, deliver, storage, now = Date.now, delay = 1000 }) {
  const key = journalKey(userId)
  let state = {cursor:0, events:[]}, active = true, timer, controller, generation = 0, failures = 0
  try {
    const saved = JSON.parse(storage?.getItem(key) || 'null')
    if (Number.isSafeInteger(saved?.cursor) && saved.cursor >= 0 && Array.isArray(saved.events) && saved.events.every(validEvent)) state = saved
  } catch {}
  const present = events => { for (const event of events) deliver(vwapNotification(event,userId,now())) }
  present(state.events)
  async function poll() {
    if (!active) return
    clearTimeout(timer); controller?.abort(); controller = new AbortController()
    const signal = controller.signal, run = ++generation
    const deadline = setTimeout(() => controller?.signal === signal && controller.abort(), 10000)
    let retry = delay
    try {
      const result = await request({events:true,after:state.cursor,signal})
      if (!active || signal.aborted || run !== generation) return
      if (result.ownerUserId !== userId || !Array.isArray(result.events) || !result.events.every(validEvent) || !Number.isSafeInteger(result.cursor) || result.cursor < state.cursor) throw new Error('Invalid notification response')
      // An acknowledgement may have been written since this request began,
      // including by another tab. Merge current saved flags before persisting.
      let saved
      try {saved=JSON.parse(storage.getItem(key) || 'null')} catch {}
      const acknowledged=new Map((Array.isArray(saved?.events)?saved.events:[]).filter(validEvent).map(e=>[e.id,presentationState(e.presentation)]))
      const previous=new Map(state.events.map(e=>[e.id,presentationState(e.presentation)]))
      const events = [...new Map([...state.events,...result.events].map(e=>[e.id,e])).values()]
        .map(e=>({...e,presentation:{...previous.get(e.id),...acknowledged.get(e.id)}}))
      const next = {cursor:result.cursor,events:events.slice(-50)}
      // A storage failure leaves the cursor in place; polling safely retries.
      storage.setItem(key,JSON.stringify(next)); state=next
      const incomingIds=new Set(result.events.map(e=>e.id))
      present(events.filter(e=>incomingIds.has(e.id)))
      failures = 0
    } catch { retry = Math.min(10000, delay * 2 ** Math.min(failures++, 4)) }
    finally {
      clearTimeout(deadline)
      if (active && run === generation) timer=setTimeout(poll,retry)
    }
  }
  return {start:poll, resume:poll, stop() {active=false;generation++;clearTimeout(timer);controller?.abort()} }
}
